import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, Alert } from 'react-native';
import { usePerformanceMonitor } from '../hooks/usePerformanceMonitor';
import { registerPerformanceCallback } from '../utils/apiPerformanceInterceptor';
import { registerNavigationCallback } from './navigation/NavigationPerformanceHelper';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { eventEmitter } from '../utils/functions.utils';
import { PERFORMANCE_MONITOR_ENABLED } from '../config/config';

/**
 * Componente visual para mostrar métricas de rendimiento en tiempo real
 * Solo se muestra en modo desarrollo
 */
const PerformanceMonitor = ({ position = 'top-right', enabled = __DEV__ }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const metrics = usePerformanceMonitor(enabled && isVisible && PERFORMANCE_MONITOR_ENABLED);
  const closeButtonRef = useRef(null);

  // Cargar estado de visibilidad desde AsyncStorage
  useEffect(() => {
    const loadVisibility = async () => {
      try {
        const saved = await AsyncStorage.getItem('perf_monitor_visible');
        if (saved !== null) {
          setIsVisible(saved === 'true');
        }
      } catch (e) {
        // Ignorar error
      }
    };
    loadVisibility();
  }, []);

  // Escuchar evento para reabrir el monitor
  useEffect(() => {
    const reopen = () => {
      console.log('[PerformanceMonitor] navigation event: reopen');
      setIsVisible(true);
      setIsMinimized(false);
      AsyncStorage.setItem('perf_monitor_visible', 'true');
    };
    const listener = eventEmitter.addListener('reopenPerformanceMonitor', reopen);
    // For web, also listen to popstate/navigation events
    if (Platform.OS === 'web') {
      window.addEventListener('popstate', reopen);
    }
    return () => {
      // listener.remove();
      listener.removeListener('reopenPerformanceMonitor', reopen);
      if (Platform.OS === 'web') {
        window.removeEventListener('popstate', reopen);
      }
    };
  }, []);

  // Registrar callbacks para actualizar métricas desde interceptores
  useEffect(() => {
    if (metrics.updateAPIResponseTime) {
      registerPerformanceCallback(metrics.updateAPIResponseTime);
    }
    if (metrics.updateNavigationTime) {
      registerNavigationCallback(metrics.updateNavigationTime);
    }
  }, [metrics.updateAPIResponseTime, metrics.updateNavigationTime]);

  // En web, agregar listener de click nativo al botón de cerrar para asegurar que funcione
  useEffect(() => {
    if (Platform.OS === 'web' && closeButtonRef.current) {
      const button = closeButtonRef.current;
      const handleNativeClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      };
      button.addEventListener('click', handleNativeClick);
      return () => {
        button.removeEventListener('click', handleNativeClick);
      };
    }
  }, []);

  const handleClose = async () => {
    try {
      console.log('[PerformanceMonitor] handleClose called');
      setIsVisible(false);
      await AsyncStorage.setItem('perf_monitor_visible', 'false');
      console.log('[PerformanceMonitor] setIsVisible(false)');
    } catch (e) {
      console.error('Error closing performance monitor:', e);
    }
  };

  const handleMinimize = () => {
    try {
      setIsMinimized(true);
    } catch (e) {
      console.error('Error minimizing performance monitor:', e);
    }
  };

  if (!enabled || !isVisible || !PERFORMANCE_MONITOR_ENABLED) return null;

  const getFPSColor = (fps) => {
    if (fps >= 55) return '#4CAF50'; // Verde - Excelente
    if (fps >= 45) return '#FFC107'; // Amarillo - Aceptable
    if (fps >= 30) return '#FF9800'; // Naranja - Bajo
    return '#F44336'; // Rojo - Crítico
  };

  const getMemoryColor = (used, total) => {
    if (total === 0) return '#9E9E9E';
    const percentage = (used / total) * 100;
    if (percentage < 60) return '#4CAF50';
    if (percentage < 80) return '#FFC107';
    return '#F44336';
  };

  const getThreadColor = (usage) => {
    if (usage < 40) return '#4CAF50'; // Verde - Normal
    if (usage < 70) return '#FFC107'; // Amarillo - Alto
    return '#F44336'; // Rojo - Crítico
  };

  const getAPIColor = (time) => {
    if (time === 0) return '#9E9E9E'; // Gris - Sin datos
    if (time < 300) return '#4CAF50'; // Verde - Rápido
    if (time < 1000) return '#FFC107'; // Amarillo - Normal
    return '#F44336'; // Rojo - Lento
  };

  const showMetricsInfo = () => {
    Alert.alert(
      '📊 Métricas de Performance',
      '🎯 FPS (Frames/sec)\n' +
      '  • Normal: 55-60\n' +
      '  • Bajo: 30-55\n' +
      '  • Crítico: <30\n\n' +
      
      '⏱️ Render Time\n' +
      '  • Óptimo: <16.67ms\n' +
      '  • Aceptable: 16-30ms\n' +
      '  • Lento: >30ms\n\n' +
      
      '🔧 JS Thread\n' +
      '  • Normal: <40%\n' +
      '  • Alto: 40-70%\n' +
      '  • Crítico: >70%\n\n' +
      
      '🎨 UI Thread\n' +
      '  • Normal: <40%\n' +
      '  • Alto: 40-70%\n' +
      '  • Crítico: >70%\n\n' +
      
      '⚠️ Slow Renders\n' +
      '  • Objetivo: 0\n' +
      '  • Renders que tardaron >100ms\n\n' +
      
      '💾 Memory (mobile estimado)\n' +
      '  • Bajo: <80MB\n' +
      '  • Medio: 80-150MB\n' +
      '  • Alto: >150MB\n\n' +
      
      '🧭 Navigation Time\n' +
      '  • Rápido: <300ms\n' +
      '  • Lento: >300ms\n\n' +
      
      '🌐 API Response\n' +
      '  • Rápido: <300ms\n' +
      '  • Normal: 300-1000ms\n' +
      '  • Lento: >1000ms',
      [{ text: 'Entendido', style: 'default' }]
    );
  };

  const positionStyles = {
    'top-right': { top: 50, right: 10 },
    'top-left': { top: 50, left: 10 },
    'bottom-right': { bottom: 50, right: 10 },
    'bottom-left': { bottom: 50, left: 10 },
  };

  if (isMinimized) {
    return (
      <TouchableOpacity
        style={[styles.minimized, positionStyles[position]]}
        onPress={() => setIsMinimized(false)}
      >
        <Text style={styles.minimizedText}>📊</Text>
      </TouchableOpacity>
    );
  }

  return (
    <View style={[styles.container, positionStyles[position]]}>
      <View style={styles.header}>
        <Text style={styles.title}>Performance Monitor</Text>
        <View style={styles.headerButtons}>
          <TouchableOpacity 
            onPress={showMetricsInfo} 
            style={styles.button}
            activeOpacity={0.7}
          >
            <Text style={styles.buttonText}>i</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            onPress={handleMinimize} 
            style={styles.button}
            activeOpacity={0.7}
          >
            <Text style={styles.buttonText}>−</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            onPress={() => {
              console.log('[PerformanceMonitor] close button pressed');
              handleClose();
            }} 
            style={styles.button}
            activeOpacity={0.7}
            accessible={true}
            accessibilityLabel="Cerrar monitor de performance"
            accessibilityRole="button"
            ref={closeButtonRef}
          >
            <Text style={styles.buttonText}>×</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.metricsContainer}>
        {/* FPS */}
        <View style={styles.metric}>
          <View style={styles.metricHeader}>
            <Text style={styles.metricLabel}>FPS</Text>
            <View style={[styles.indicator, { backgroundColor: getFPSColor(metrics.fps) }]} />
          </View>
          <Text style={[styles.metricValue, { color: getFPSColor(metrics.fps) }]}>
            {metrics.fps}
          </Text>
          <Text style={styles.metricTarget}>Target: 60</Text>
        </View>

        {/* Render Time */}
        <View style={styles.metric}>
          <View style={styles.metricHeader}>
            <Text style={styles.metricLabel}>Render</Text>
            <View style={[styles.indicator, { backgroundColor: metrics.renderTime < 16.67 ? '#4CAF50' : '#F44336' }]} />
          </View>
          <Text style={styles.metricValue}>
            {metrics.renderTime.toFixed(1)}ms
          </Text>
          <Text style={styles.metricTarget}>Target: &lt;16.67ms</Text>
        </View>

        {/* JS Thread */}
        <View style={styles.metric}>
          <View style={styles.metricHeader}>
            <Text style={styles.metricLabel}>JS Thread</Text>
            <View style={[styles.indicator, { backgroundColor: getThreadColor(metrics.jsThreadUsage) }]} />
          </View>
          <Text style={[styles.metricValue, { color: getThreadColor(metrics.jsThreadUsage) }]}>
            {metrics.jsThreadUsage}%
          </Text>
          <Text style={styles.metricTarget}>Target: &lt;40%</Text>
        </View>

        {/* UI Thread */}
        <View style={styles.metric}>
          <View style={styles.metricHeader}>
            <Text style={styles.metricLabel}>UI Thread</Text>
            <View style={[styles.indicator, { backgroundColor: getThreadColor(metrics.uiThreadUsage) }]} />
          </View>
          <Text style={[styles.metricValue, { color: getThreadColor(metrics.uiThreadUsage) }]}>
            {metrics.uiThreadUsage}%
          </Text>
          <Text style={styles.metricTarget}>Target: &lt;40%</Text>
        </View>

        {/* Slow Renders */}
        <View style={styles.metric}>
          <View style={styles.metricHeader}>
            <Text style={styles.metricLabel}>Slow Renders</Text>
            <View style={[styles.indicator, { backgroundColor: metrics.slowRenders === 0 ? '#4CAF50' : '#F44336' }]} />
          </View>
          <Text style={[styles.metricValue, { color: metrics.slowRenders === 0 ? '#4CAF50' : '#F44336' }]}>
            {metrics.slowRenders}
          </Text>
          <Text style={styles.metricTarget}>&gt;100ms</Text>
        </View>

        {/* Memory - Web: exacto, Mobile: estimado */}
        {(Platform.OS === 'web' && metrics.memory.total > 0) || Platform.OS !== 'web' ? (
          <View style={styles.metric}>
            <View style={styles.metricHeader}>
              <Text style={styles.metricLabel}>Memory</Text>
              <View style={[
                styles.indicator,
                { backgroundColor: getMemoryColor(metrics.memory.used || metrics.jsHeapSize, metrics.memory.total || 200) }
              ]} />
            </View>
            <Text style={styles.metricValue}>
              {Platform.OS === 'web' ? metrics.memory.used : `~${metrics.jsHeapSize || 45}`}MB
            </Text>
            <Text style={styles.metricTarget}>
              {Platform.OS === 'web' 
                ? `${metrics.memory.total}MB / ${metrics.jsHeapSize}MB`
                : 'Estimated'
              }
            </Text>
          </View>
        ) : null}

        {/* Navigation Time */}
        {metrics.navigationTime > 0 && (
          <View style={styles.metric}>
            <View style={styles.metricHeader}>
              <Text style={styles.metricLabel}>Navigation</Text>
              <View style={[styles.indicator, { backgroundColor: metrics.navigationTime < 300 ? '#4CAF50' : '#F44336' }]} />
            </View>
            <Text style={styles.metricValue}>
              {metrics.navigationTime}ms
            </Text>
            <Text style={styles.metricTarget}>Last screen</Text>
          </View>
        )}

        {/* API Response Time - SIEMPRE VISIBLE */}
        <View style={styles.metric}>
          <View style={styles.metricHeader}>
            <Text style={styles.metricLabel}>API Response</Text>
            <View style={[styles.indicator, { backgroundColor: getAPIColor(metrics.apiResponseTime) }]} />
          </View>
          <Text style={[styles.metricValue, { color: getAPIColor(metrics.apiResponseTime) }]}>
            {metrics.apiResponseTime > 0 ? `${metrics.apiResponseTime}ms` : '-'}
          </Text>
          <Text style={styles.metricTarget} numberOfLines={1} ellipsizeMode="tail">
            {metrics.lastApiEndpoint || 'No requests'}
          </Text>
        </View>
      </View>

      {/* Platform Info */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Platform: {Platform.OS} {Platform.Version}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    borderRadius: 9,
    padding: 9,
    minWidth: 245,
    maxWidth: 274,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.3,
    shadowRadius: 5,
    elevation: 8,
    zIndex: 9999,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 9,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.1)',
  },
  title: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  headerButtons: {
    flexDirection: 'row',
    gap: 5,
  },
  button: {
    width: Platform.OS === 'web' ? 24 : 18,
    height: Platform.OS === 'web' ? 24 : 18,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 3,
    cursor: Platform.OS === 'web' ? 'pointer' : 'default',
  },
  buttonText: {
    color: '#fff',
    fontSize: Platform.OS === 'web' ? 14 : 12,
    fontWeight: 'bold',
    userSelect: 'none',
  },
  metricsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 5,
  },
  metric: {
    width: '31%',
    alignItems: 'center',
    marginBottom: 5,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    marginBottom: 3,
  },
  metricLabel: {
    color: '#9E9E9E',
    fontSize: 7,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  indicator: {
    width: 5,
    height: 5,
    borderRadius: 2.5,
  },
  metricValue: {
    color: '#fff',
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 1,
  },
  metricTarget: {
    color: '#757575',
    fontSize: 6,
    maxWidth: '100%',
    textAlign: 'center',
  },
  footer: {
    marginTop: 7,
    paddingTop: 5,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255, 255, 255, 0.1)',
  },
  footerText: {
    color: '#757575',
    fontSize: 8,
    textAlign: 'center',
  },
  minimized: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 5,
    zIndex: 9999,
  },
  minimizedText: {
    fontSize: 14,
  },
});

export default PerformanceMonitor;
